import React from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';

const cache = {};

/**
 * use this to move the fetching of data to a HOC (High-Order-Component)
 * 3 basic use cases are covered
 *
 * a) within the component to fetch data without writing the same code over and over again
 * b) within the module to separate fetching of data and the rendering of the component
 * c) within the parent component, fetched data and a component with zero effort
 *
 * @param  {node} Component       component to render with the loaded data
 * @param  {object} optionalSetup optional input => check loadMultipleFiles for full description of it structure
 * @return {object}                a class to use in the parents render Method
 */
export default (Component, optionalSetup) => {
    return class Loader extends React.Component {
        static propTypes = {
            loaderConfig: PropTypes.object
        };

        static defaultProps = {
            loaderConfig: undefined
        };

        /**
         * @param  {object} props
         */
        constructor(props) {
            super(props);

            this.state = {};

            if (optionalSetup) {
                this.loadMultipleFiles(optionalSetup);
            } else if (props.loaderConfig) {
                this.loadMultipleFiles(props.loaderConfig);
            }
        }

        /**
         * trigger the fetching of data and internally forward the result to the child component
         *
         * there is no loading state, by intention otherwise the component would re-render every time the loading state
         * would change. instead we check if the prop is available otherwise it is still loading.
         *
         * @param  {string}  key              name of the prop, the result should get forwarded too
         * @param  {string}  url              url to fetch from
         * @param  {string|boolean} cacheKey  optional key to cache the result of the url to, use this for data that dose not changes on runtime, pass the url here if you like
         * @param  {function} reducer         optional function to shrink the data coming from the url to what ever you need
         */
        load = (key, url, cacheKey = false, reducer = false) => {
            // check the cache
            if (cacheKey && cache[cacheKey]) {
                // setTimeout makes sure the setState happens asynchronous
                setTimeout(
                    this.updateStateWithFile, 0, key, cache[cacheKey], url, reducer
                );
                return;
            }
            // fetch the file and handle success or error state
            axios.get(url).then(
                (response) => {
                    if (cacheKey) {
                        cache[cacheKey] = response.data;
                    }
                    this.updateStateWithFile(key, response.data, url, reducer);
                }
            ).catch((error) => {
                this.setState({
                    [key]: {
                        data: undefined,
                        error
                    }
                });
            });
        };

        /**
         * this function is called by this.load only!
         * its a trigger to store the requested data in the state.
         * its an extra function to avoid duplicated code and remove the risk of having a different behavior for cached
         * and fetched data
         *
         * @param  {string} key         name of the prop the result should get forwarded to
         * @param  {any} data           result of the ajax call
         * @param  {string} url         url as string
         * @param  {function} reducer   optional function to transform the result
         */
        updateStateWithFile = (key, data, url, reducer) => {
            this.setState({
                [key]: {
                    // if a reducer is passed, let it process the data
                    data: (reducer && reducer(data, url)) || data,
                    error: undefined
                }
            });
        };

        /**
         * load multiple files with one shoot
         *
         * the key will be used as the props name for the child component
         * the value should contain an object with at least a url specified
         * optionally you can put an cacheKey and a reducer function
         *
         * PropTypes.shape({
         *     url: PropTypes.string.isRequired,
         *     cacheKey: PropTypes.string,
         *     reducer: PropTypes.func
         * })
         *
         * Example:
         * loadMultipleFiles({
         *      absoluteExample:{
         *          url: "http://test.com/first/"
         *          }
         *      relativeExample:{
         *          url: "/first/",
         *          cacheKey: "relativeFirst",
         *          reducer: (data) => {
         *              return data.something.specific;
         *          }
         *      }
         * })
         *
         * after fetching the stuff from the given url it will be passed to the child like
         *
         * <Component
         *     absoluteExample={this.state.absoluteExample}
         *     relativeExample={this.state.relativeExample}
         *     someGivenProp={this.props.someGivenProp}
         * />
         *
         *
         * @param  {object} setup key:value in the format propName:url
         */
        loadMultipleFiles = (setup) => {
            if (!setup || typeof setup !== 'object') {
                throw new Error('missing input for loadMultipleFiles');
            }

            const keys = Object.keys(setup);

            if (!keys.length) {
                throw new Error('the given setup is an empty object');
            }

            keys.forEach(
                (key) => {
                    if (!setup[key].url) {
                        throw new Error('missing url in the given config');
                    }
                    this.load(
                        key,
                        setup[key].url,
                        setup[key].cacheKey,
                        setup[key].reducer
                    );
                }
            );
        };

        /**
         * clear the cached file for one specific cacheKey
         *
         * @param  {string} cacheKey
         * @return {boolean}     was the clearing success full or not
         */
        clearCache = (cacheKey) => {
            if (!cacheKey || !cache[cacheKey]) {
                return false;
            }
            delete cache[cacheKey];
            return true;
        };

        /**
         * renders the given component with the current state of the fetched data
         * and forwards the props given by the parent
         * plus a little fetchApi to load some more things from inside of the component (if necessary)
         *
         * @return {node}
         */
        render() {
            return (
                <Component
                    {...this.state}
                    {...this.props}
                    fetchApi={{
                        load: this.load,
                        loadMultipleFiles: this.loadMultipleFiles,
                        clearCache: this.clearCache
                    }}
                />
            );
        }
    };
};
